/**
 * Decides whether the From on a saved interview is still a sender we can use,
 * and if not, why (ENG-3916).
 *
 * A scheduled interview keeps whoever originally scheduled it as the sender.
 * By the time someone edits it that user may have been deactivated, or turned
 * off email delegation — either way the backend rejects the save, so the
 * wizard has to catch it first and say which it is.
 *
 * Pure: the caller supplies the currently-valid sender values (the
 * `email_delegatable` set, plus shared mailboxes) and the saved sender object
 * from the interview payload.
 */
import { isSharedEmailFromValue } from '../hooks/useScheduleSenderOptions';
import {
  getSenderErrorMessage,
  type SenderErrorReason,
  type SenderUseCase,
} from './senderErrorCopy';

/** Sender as serialised by the participant partial. */
export interface SavedSender {
  id?: number | string;
  full_name?: string | null;
  /** `User` status enum — anything other than `active` can't send. */
  status?: string | null;
  /**
   * Labelling only — the rule below reads neither. They are here because the
   * From picker stocks only CURRENTLY delegatable senders, so a saved one that
   * has dropped out has nothing left to render it with and the field goes blank
   * over a value that is really there.
   */
  email?: string | null;
  avatar_url?: string | null;
}

export interface SenderValidityArgs {
  /** Current form value: a user id, a `shared:<id>` value, or empty. */
  from: string | number | null | undefined;
  /**
   * Values currently offered by the From picker. Missing (or empty) means the
   * caller can't tell yet — treated as "no issue" rather than "all invalid".
   */
  validFromValues?: Set<string> | null;
  /** Sender object hydrated from the interview, when there is one. */
  savedSender?: SavedSender | null;
  /**
   * `false` while the delegatable-senders query is in flight — every value
   * looks invalid then, so hold off rather than flag a good sender.
   */
  isFromOptionsFetched?: boolean;
  /**
   * The signed-in user. The From is seeded with them whether or not they can
   * send, so that they can SEE who the email would come from — which makes
   * "the sender is absent from the delegatable list" mean something different
   * for them than for anyone else. See `getSenderIssue`.
   */
  currentUserId?: number | string | null;
}

/**
 * `undefined` when the sender is usable (or we can't tell yet).
 */
export function getSenderIssue({
  from,
  validFromValues,
  savedSender,
  isFromOptionsFetched = true,
  currentUserId,
}: SenderValidityArgs): SenderErrorReason | undefined {
  // Empty is the existing "From is required" case, not this one.
  if (from === null || from === undefined || String(from).trim() === '') {
    return undefined;
  }
  // Shared mailboxes carry no delegation or user status.
  if (isSharedEmailFromValue(String(from))) return undefined;
  if (!isFromOptionsFetched || !validFromValues || validFromValues.size === 0) {
    return undefined;
  }

  if (validFromValues.has(String(from))) return undefined;

  // `filter_by_email_delegatable` always keeps the REQUESTING user — nobody
  // grants themselves delegation — so the scheduler's own absence can only be a
  // disconnected mailbox, and that is a fix they can perform themselves.
  if (
    currentUserId !== null &&
    currentUserId !== undefined &&
    String(currentUserId) === String(from)
  ) {
    return 'mailboxNotConnected';
  }

  // Deactivated users are excluded from the delegatable scope AND from the
  // active-user search, so an absent sender whose saved record says
  // non-active is deactivated; anything else absent is delegation.
  const status = savedSender?.status;
  if (typeof status === 'string' && status && status !== 'active') {
    return 'deactivated';
  }
  return 'delegation';
}

export interface SenderErrorArgs extends SenderValidityArgs {
  useCase: SenderUseCase;
}

/** The field error to show, or `undefined` when the sender is fine. */
export function getSenderFieldError({
  useCase,
  savedSender,
  ...validity
}: SenderErrorArgs): string | undefined {
  const reason = getSenderIssue({ ...validity, savedSender });
  if (!reason) return undefined;
  return getSenderErrorMessage({
    reason,
    useCase,
    userName: savedSender?.full_name,
  });
}
